/**
 * Guard against automatic login-redirect loops (GLX-1743).
 *
 * When authentication cannot complete, Galaxy signs the user out and sends
 * them to the login app automatically. If the login app immediately redirects
 * back with a token that keeps failing to exchange (e.g. rails-api outage),
 * the two apps would ping-pong forever. Automatic redirects are tracked in
 * sessionStorage — it survives the cross-domain round trip within the tab and
 * is NOT wiped by SIGN_OUT's localStorage.clear() — and auto-redirecting stops
 * once the limit is hit, leaving the user a manual "Return to login" button.
 */

const STORAGE_KEY = 'galaxy_auto_login_redirects';
const WINDOW_MS = 60_000;
const MAX_AUTO_REDIRECTS = 2;

const readRecentRedirects = (): number[] => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed.filter(
      (timestamp): timestamp is number =>
        typeof timestamp === 'number' && Date.now() - timestamp < WINDOW_MS,
    );
  } catch {
    // malformed JSON or sessionStorage unavailable
    return [];
  }
};

export const canAutoRedirectToLogin = (): boolean => {
  return readRecentRedirects().length < MAX_AUTO_REDIRECTS;
};

export const recordAutoLoginRedirect = (): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...readRecentRedirects(), Date.now()]));
  } catch {
    // sessionStorage unavailable — skip tracking
  }
};
